import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col, Card, Spinner, Badge, Tabs, Tab, Button, Table, Pagination, Modal, Form } from "react-bootstrap";
import {
  FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaVenusMars,
  FaGraduationCap, FaSchool, FaIdCard, FaArrowLeft, FaEdit,
  FaCheckCircle, FaTimesCircle, FaBook, FaLanguage, FaClock, FaEye, FaTrash, FaPlus
} from "react-icons/fa";
import Select from "react-select";
import { UserContext } from "../../context/UserContextProvider";
import APIServicenew from "../../utils/APIGeneralService";
import { ToastContainer, toast } from "react-toastify";
import { formateDatemdy, capitalizeFirstLetterOfEveryWord } from "../../utils/Helper";
import useMenuPermissions from "../../components/CustomHook/custompermission";

const StudentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const permissionscontextCourse = useMenuPermissions("/admin/course");
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, logout } = useContext(UserContext);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [loadingPassword, setLoadingPassword] = useState(false);
  const headers = {
    "auth-token": authToken,
    "authorization": accessToken
  };

  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAssignCourse, setSelectedAssignCourse] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loading, setLoading] = useState(false);
  const [studentData, setStudentData] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [attemptQuizData, setAttemptQuizData] = useState([]);
  const [loadingQuiz, setLoadingQuiz] = useState(false);

  // Add with other state declarations (around line 70)
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingLoginHistory, setLoadingLoginHistory] = useState(false);
  const [loginHistoryTotal, setLoginHistoryTotal] = useState(0);
  const [loginHistoryPage, setLoginHistoryPage] = useState(1);
  const loginHistoryLimit = 10;
  // New state for learned courses
  const [learnedCourses, setLearnedCourses] = useState({ courses: [], total: 0 });
  const [loadingLearnedCourses, setLoadingLearnedCourses] = useState(false);

  // Local pagination for quizzes
  const [quizCurrentPage, setQuizCurrentPage] = useState(1);
  const quizLimit = 10;
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);

  // Local pagination for courses
  const [coursesCurrentPage, setCoursesCurrentPage] = useState(1);
  const coursesLimit = 10;

  useEffect(() => {
    if (id) {
      fetchStudentDetails();
    }
  }, [id]);

  useEffect(() => {
    if (activeTab === 'attempt-quiz' && id) {
      fetchAttemptQuiz();
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === 'learned-courses' && id) {
      fetchLearnedCourses();
    }
  }, [activeTab, id, coursesCurrentPage]);

  useEffect(() => {
    setQuizCurrentPage(1);
  }, [attemptQuizData]);

  // Get paginated quizzes
  const getPaginatedQuizzes = () => {
    const startIndex = (quizCurrentPage - 1) * quizLimit;
    const endIndex = startIndex + quizLimit;
    return attemptQuizData.slice(startIndex, endIndex);
  };

  const totalQuizzes = attemptQuizData.length;
  const totalQuizPages = Math.ceil(totalQuizzes / quizLimit);
  const totalLoginHistoryPages = Math.ceil(loginHistoryTotal / loginHistoryLimit);

  const fetchStudentDetails = async () => {
    setLoading(true);
    try {
      const response = await APIServices.get(
        `/admin/student/${id}`,
        headers
      );

      if (response?.data?.status === true) {
        setStudentData(response?.data?.data);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        showToast(response?.data?.message || "Error fetching student details", "error");
      }
    } catch (error) {
      console.error("Error fetching student details:", error);
      showToast("Error fetching student details", "error");
    } finally {
      setLoading(false);
    }
  };

  // New function to fetch learned courses
  const fetchLearnedCourses = async () => {
    setLoadingLearnedCourses(true);
    try {
      const skip = (coursesCurrentPage - 1) * coursesLimit;
      const response = await APIServices.get(
        `/admin/student/learn-courses/${id}?skip=${skip}&limit=${coursesLimit}`,
        headers
      );

      if (response?.data?.status === true) {
        setLearnedCourses({
          courses: response?.data?.data?.courses || [],
          total: response?.data?.data?.total || 0
        });
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        showToast(response?.data?.message || "Error fetching learned courses", "error");
      }
    } catch (error) {
      console.error("Error fetching learned courses:", error);
      showToast("Error fetching learned courses", "error");
    } finally {
      setLoadingLearnedCourses(false);
    }
  };

  const fetchAttemptQuiz = async () => {
    setLoadingQuiz(true);
    try {
      const response = await APIServices.get(
        `/admin/student/students_attempt_quiz/${id}`,
        headers
      );

      if (response?.data?.status === true) {
        setAttemptQuizData(response?.data?.data?.data || []);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        showToast(response?.data?.message || "Error fetching attempt quiz data", "error");
      }
    } catch (error) {
      console.error("Error fetching attempt quiz:", error);
      showToast("Error fetching attempt quiz data", "error");
    } finally {
      setLoadingQuiz(false);
    }
  };

  const fetchAllCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await APIServices.get(
        `/admin/courses/short/list`,
        headers
      );

      if (response?.data?.status === true) {
        const coursesData = response?.data?.data?.data || [];
        const formattedCourses = coursesData.map(course => ({
          value: course.id,
          label: `${course.title} (${course.unique_course_id})`
        }));
        setAllCourses(formattedCourses);
      }
    } catch (error) {
      console.error("Error fetching courses:", error);
      showToast("Error fetching courses", "error");
    } finally {
      setLoadingCourses(false);
    }
  };


  // Add this function after fetchAttemptQuiz
  const fetchLoginHistory = async () => {
    setLoadingLoginHistory(true);
    try {
      const skip = (loginHistoryPage - 1) * loginHistoryLimit;
      const response = await APIServices.get(
        `/admin/student/login-history/${id}?skip=${skip}&limit=${loginHistoryLimit}`,
        headers
      );

      if (response?.data?.status === true) {
        setLoginHistory(response?.data?.data?.data || []);
        setLoginHistoryTotal(response?.data?.data?.total || 0);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        showToast(response?.data?.message || "Error fetching login history", "error");
      }
    } catch (error) {
      console.error("Error fetching login history:", error);
      showToast("Error fetching login history", "error");
    } finally {
      setLoadingLoginHistory(false);
    }
  };

  // Add with other useEffect hooks (around line 100)
  useEffect(() => {
    if (activeTab === 'login-history' && id) {
      fetchLoginHistory();
    }
  }, [activeTab, id, loginHistoryPage]);

  const handleAssignCourse = async () => {
    if (!selectedAssignCourse) {
      showToast("Please select a course", "error");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("student_id", studentData.id);
      formData.append("course_id", selectedAssignCourse.value);

      const response = await APIServices.post(
        "/admin/student/assign_course",
        formData,
        {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response?.data?.status === true) {
        const successMessage = response?.data?.message || "Course assigned successfully";
        showToast(successMessage, "success");
        setShowAssignModal(false);
        setSelectedAssignCourse(null);
        // Refresh both student details and learned courses
        fetchStudentDetails();
        if (activeTab === 'learned-courses') {
          fetchLearnedCourses();
        }
      } else {
        const errorMessage = response?.data?.message || "Failed to assign course";
        showToast(errorMessage, "error");
      }
    } catch (error) {
      console.error("Error assigning course:", error);
      const errorMessage = error?.response?.data?.message || "Error assigning course to student";
      showToast(errorMessage, "error");
    }
  };

  useEffect(() => {
    if (showAssignModal) {
      fetchAllCourses();
    }
  }, [showAssignModal]);

  const handleRemoveCourse = async () => {
    try {
      const formData = new FormData();
      formData.append("student_id", studentData.id);
      formData.append("course_id", selectedCourse.id);

      const response = await APIServices.delete(
        "/admin/student/remove_course",
        formData,
        {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response?.data?.status === true) {
        showToast(response?.data?.message || "Course removed successfully", "success");
        setSelectedCourse(null);
        fetchStudentDetails();
        if (activeTab === 'learned-courses') {
          fetchLearnedCourses();
        }
      } else {
        showToast(response?.data?.message || "Something went wrong", "error");
      }
    } catch (error) {
      console.error("Error removing course:", error);
      showToast("Error removing course from student", "error");
    }
  };

  const getStatusBadge = (status) => {
    if (status === "Active") {
      return <Badge bg="success" className="px-3 py-2">Active</Badge>;
    } else if (status === "Inactive") {
      return <Badge bg="danger" className="px-3 py-2">Inactive</Badge>;
    }
    return <Badge bg="secondary" className="px-3 py-2">Unknown</Badge>;
  };

  const totalCourses = learnedCourses.total;
  const totalPages = Math.ceil(totalCourses / coursesLimit);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading student details...</p>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div className="text-center py-5">
        <p>No data found</p>
        <Button variant="primary" onClick={() => navigate("/admin/student")}>
          Back to Students
        </Button>
      </div>
    );
  }

  const showToast = (message, type) => {
    toast(message, {
      position: "top-right",
      autoClose: 2000,
      type,
      hideProgressBar: false,
      closeOnClick: true,
    });
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      showToast("Password must be at least 6 characters", "error");
      return;
    }

    setLoadingPassword(true);
    try {
      const formData = new FormData();
      formData.append("id", studentData.id);
      formData.append("new_password", newPassword);

      const response = await APIServices.put(
        "/admin/student/change_password",
        formData,
        {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      );

      if (response?.data?.status === true) {
        showToast(response?.data?.message || "Password changed successfully", "success");
        setShowPasswordModal(false);
        setNewPassword("");
      } else {
        showToast(response?.data?.message || "Failed to change password", "error");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      const errorMessage = error?.response?.data?.message || "Error changing password";
      showToast(errorMessage, "error");
    } finally {
      setLoadingPassword(false);
    }
  };

  return (
    <>
      <div className="student-details-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 className="font_blue_18 font-600 ms-2">
            Student Details
          </h1>

          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/admin/student")}
            >
              <FaArrowLeft className="me-1" /> Back
            </Button>
          </div>
        </div>

        <Row>
          {/* Left Sidebar - 4 Columns - Single Card */}
          <Col md={4}>
            <Card>
              <Card.Body>
                {/* Profile Picture */}
                <div className="text-center mb-3">
                  {studentData.profile_pic ? (
                    <img
                      src={studentData.profile_pic}
                      alt={studentData.first_name}
                      style={{
                        width: '150px',
                        height: '150px',
                        objectFit: 'cover',
                        borderRadius: '50%',
                        marginBottom: '10px'
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '150px',
                        height: '150px',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 10px auto'
                      }}
                    >
                      <FaUser size={60} color="#999" />
                    </div>
                  )}
                  <h4><strong>{capitalizeFirstLetterOfEveryWord(studentData.first_name)} {capitalizeFirstLetterOfEveryWord(studentData.last_name)}</strong></h4>
                  <p className="text-muted">Roll No: {studentData.roll_number}</p>
                  <div className="mt-2">
                    {getStatusBadge(studentData.status)}
                  </div>
                </div>

                <hr />

                {/* Student Details */}
                <div className="student-info">
                  <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                    <strong>Roll Number:</strong>
                    <span>{studentData.roll_number}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                    <strong>Email:</strong>
                    <span>{studentData.email || 'N/A'}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                    <strong>Mobile:</strong>
                    <span>{studentData.country_code}-{studentData.mobile || 'N/A'}</span>
                  </div>
                  {/* <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                    <strong>Password:</strong>
                    <span className="font-monospace text-muted">{studentData.password || "—"}</span>
                  </div> */}
                  <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                    <strong>Father's Name:</strong>
                    <span>{studentData.father_name || 'N/A'}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                    <strong>Date of Birth:</strong>
                    <span>{studentData.dob ? formateDatemdy(studentData.dob) : 'N/A'}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                    <strong>Gender:</strong>
                    <span>{studentData.gender || 'N/A'}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-3 pb-2 border-bottom">
                    <strong>Created At:</strong>
                    <span>{formateDatemdy(studentData.created_at)}</span>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Change Password Card */}
            {/* <Card className="mt-3">
              <Card.Body>
                <Button
                  variant="outline-primary"
                  onClick={() => setShowPasswordModal(true)}
                  className="w-100"
                >
                  <FaEdit className="me-1" /> Change Password
                </Button>
              </Card.Body>
            </Card> */}
          </Col>

          {/* Right Content - 8 Columns with Tabs */}
          <Col md={8}>
            <Card>
              <Card.Body>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-3"
                >
                  {/* Tab 1: Student Info */}
                  <Tab eventKey="info" title="Student Info">
                    <div className="p-3">
                      <div className="card p-3 shadow-sm">
                        <h6 className="text-primary mb-3">Student Information</h6>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">First Name</div>
                            <div className="fw-semibold">{studentData.first_name || 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Last Name</div>
                            <div className="fw-semibold">{studentData.last_name || 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Father's Name</div>
                            <div className="fw-semibold">{studentData.father_name || 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Gender</div>
                            <div className="fw-semibold">{studentData.gender || 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Date of Birth</div>
                            <div className="fw-semibold">{studentData.dob ? formateDatemdy(studentData.dob) : 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Roll Number</div>
                            <div className="fw-semibold">{studentData.roll_number || 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Email</div>
                            <div className="fw-semibold">{studentData.email || 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Mobile</div>
                            <div className="fw-semibold">{studentData.mobile || 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">School</div>
                            <div className="fw-semibold">{studentData.school_name || 'N/A'}</div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <div className="text-muted small">Grade</div>
                            <div className="fw-semibold">
                              <Badge bg="info">{studentData.grade_name || 'N/A'}</Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Tab>

                  {/* Tab 2: Learned Courses - Updated */}
                  <Tab eventKey="learned-courses" title={`Learned Course${totalCourses !== 1 ? 's' : ''} `}>
                    <div className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="text-primary mb-0">Learned Courses ({totalCourses})</h6>
                        <div className="d-flex gap-2">
                          {permissionscontextCourse.add === 1 && 
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setShowAssignModal(true)}
                          >
                            <FaPlus className="me-1" /> Assign Course
                          </Button>}
                        </div>
                      </div>

                      {loadingLearnedCourses ? (
                        <div className="text-center py-4">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2">Loading learned courses...</p>
                        </div>
                      ) : learnedCourses.courses?.length > 0 ? (
                        <>
                          <Table responsive className="table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Image</th>
                                <th>Course ID</th>
                                <th>Title</th>
                                <th>Duration</th>
                                <th>Language</th>
                                <th>Topics/Lessons</th>
                                <th>First Accessed</th>
                                <th>Last Accessed</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {learnedCourses.courses.map((course, index) => (
                                <tr key={course.id}>
                                  <td>{((coursesCurrentPage - 1) * coursesLimit) + index + 1}</td>
                                  <td>
                                    {course.image ? (
                                      <img
                                        src={course.image}
                                        alt={course.title}
                                        style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                                      />
                                    ) : (
                                      <div style={{ width: '50px', height: '50px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}></div>
                                    )}
                                  </td>
                                  <td>{course.unique_course_id}</td>
                                  <td>
                                    <strong>{course.title}</strong>

                                  </td>
                                  <td>{course.duration || 'N/A'}</td>
                                  <td>{course.language || 'N/A'}</td>
                                  <td>
                                    <Badge bg="info" className="me-1">Topics: {course.no_of_topics || 0}</Badge>
                                    <Badge bg="secondary">Lessons: {course.no_of_lessons || 0}</Badge>
                                  </td>
                                  <td>{course.first_accessed ? formateDatemdy(course.first_accessed) : 'N/A'}</td>
                                  <td>{course.last_accessed ? formateDatemdy(course.last_accessed) : 'N/A'}</td>
                                  <td>
                                    <div className="d-flex gap-2">

                                      <Button
                                        variant="outline-info"
                                        size="sm"
                                        onClick={() => navigate(`/admin/student-course-performance/${studentData.id}/${course.id}`)}
                                        title="View Course Performance"
                                      >
                                        <FaEye />
                                      </Button>

                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>

                          {/* Pagination for Courses */}
                          {totalPages > 1 && (
                            <div className="d-flex justify-content-end mt-3">
                              <Pagination>
                                <Pagination.Prev
                                  disabled={coursesCurrentPage === 1}
                                  onClick={() => setCoursesCurrentPage(prev => prev - 1)}
                                />
                                {[...Array(totalPages)].map((_, i) => (
                                  <Pagination.Item
                                    key={i + 1}
                                    active={coursesCurrentPage === i + 1}
                                    onClick={() => setCoursesCurrentPage(i + 1)}
                                  >
                                    {i + 1}
                                  </Pagination.Item>
                                ))}
                                <Pagination.Next
                                  disabled={coursesCurrentPage === totalPages}
                                  onClick={() => setCoursesCurrentPage(prev => prev + 1)}
                                />
                              </Pagination>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted">No courses assigned to this student</p>
                        </div>
                      )}
                    </div>
                  </Tab>
                  {/* Tab 3: Login History */}
                  <Tab eventKey="login-history" title={`Login History `}>
                    <div className="p-3">
                      {loadingLoginHistory ? (
                        <div className="text-center py-4">
                          <Spinner animation="border" variant="primary" />
                          <p className="mt-2">Loading login history...</p>
                        </div>
                      ) : loginHistory.length > 0 ? (
                        <>
                          <Table responsive className="table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Student Name</th>
                                <th>Email</th>
                                <th>Roll Number</th>
                                <th>Action</th>
                                <th>Description</th>
                                <th>IP Address</th>
                                <th>Login Time</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loginHistory.map((log, index) => (
                                <tr key={log.id}>
                                  <td>{((loginHistoryPage - 1) * loginHistoryLimit) + index + 1}</td>
                                  <td>{log.student_name || 'N/A'}</td>
                                  <td>{log.student_email || 'N/A'}</td>
                                  <td>{log.student_roll_number || 'N/A'}</td>
                                  <td>
                                    <Badge bg={log.action === 'LOGIN' ? 'success' : 'info'}>
                                      {log.action || 'N/A'}
                                    </Badge>
                                  </td>
                                  <td>{log.description || 'N/A'}</td>
                                  <td>
                                    <code>{log.ip_address || 'N/A'}</code>
                                  </td>
                                  <td>{log.login_time ? formateDatemdy(log.login_time) : 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>

                          {/* Pagination for Login History */}
                          {totalLoginHistoryPages > 1 && (
                            <div className="d-flex justify-content-end mt-3">
                              <Pagination>
                                <Pagination.Prev
                                  disabled={loginHistoryPage === 1}
                                  onClick={() => setLoginHistoryPage(prev => prev - 1)}
                                />
                                {[...Array(totalLoginHistoryPages)].map((_, i) => (
                                  <Pagination.Item
                                    key={i + 1}
                                    active={loginHistoryPage === i + 1}
                                    onClick={() => setLoginHistoryPage(i + 1)}
                                  >
                                    {i + 1}
                                  </Pagination.Item>
                                ))}
                                <Pagination.Next
                                  disabled={loginHistoryPage === totalLoginHistoryPages}
                                  onClick={() => setLoginHistoryPage(prev => prev + 1)}
                                />
                              </Pagination>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted">No login history found for this student</p>
                        </div>
                      )}
                    </div>
                  </Tab>

                </Tabs>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>



      {/* Assign Course Modal */}
      <Modal
        show={showAssignModal}
        onHide={() => {
          setShowAssignModal(false);
          setSelectedAssignCourse(null);
        }}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Course to Student</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Student</Form.Label>
              <Form.Control
                type="text"
                value={`${studentData?.first_name} ${studentData?.last_name} (${studentData?.roll_number})`}
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select Course <span className="text-danger">*</span></Form.Label>
              {loadingCourses ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Loading courses...</span>
                </div>
              ) : (
                <Select
                  options={allCourses}
                  value={selectedAssignCourse}
                  onChange={setSelectedAssignCourse}
                  placeholder="Search and select a course..."
                  isClearable
                  isSearchable
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? `No courses found matching "${inputValue}"` : "Type to search courses..."
                  }
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#ced4da',
                      '&:hover': {
                        borderColor: '#80bdff'
                      }
                    })
                  }}
                />
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAssignModal(false);
            setSelectedAssignCourse(null);
          }}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAssignCourse}>
            Assign Course
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Quiz Details Modal */}
      <Modal
        show={showQuizModal}
        onHide={() => {
          setShowQuizModal(false);
          setSelectedQuiz(null);
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Quiz Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedQuiz && (
            <div>
              <Row>
                <Col md={6} className="mb-3">
                  <div className="text-muted small">Topic Name</div>
                  <div className="fw-semibold">{selectedQuiz.topic_name || 'N/A'}</div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="text-muted small">Lesson Name</div>
                  <div className="fw-semibold">{selectedQuiz.lesson_name || 'N/A'}</div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="text-muted small">Quiz Score</div>
                  <div className="fw-semibold">{selectedQuiz.quiz_score ?? 'N/A'}</div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="text-muted small">Status</div>
                  <div>
                    {selectedQuiz.result_status ? (
                      <Badge bg={selectedQuiz.result_status === 'Pass' ? 'success' : selectedQuiz.result_status === 'Fail' ? 'danger' : 'warning'}>
                        {selectedQuiz.result_status}
                      </Badge>
                    ) : 'N/A'}
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="text-muted small">Attempt Count</div>
                  <div className="fw-semibold">{selectedQuiz.attempt_count ?? 'N/A'}</div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="text-muted small">Date Attempted</div>
                  <div className="fw-semibold">{selectedQuiz.date ? formateDatemdy(selectedQuiz.date) : 'N/A'}</div>
                </Col>
              </Row>
              {selectedQuiz.quiz_question_answer && selectedQuiz.quiz_question_answer.length > 0 && (
                <div className="mt-4" style={{ height: "300px", overflow: "auto" }}>
                  <Table responsive className="table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Question</th>
                        <th>Student Answer</th>
                        <th>Status</th>
                        <th>Attempted At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuiz.quiz_question_answer.map((item, index) => (
                        <tr key={item.quiz_id || index}>
                          <td>{index + 1}</td>
                          <td>{item.question || 'N/A'}</td>
                          <td>{item.student_answer || 'N/A'}</td>
                          <td>
                            {item.is_correct ? (
                              <Badge bg="success">Correct</Badge>
                            ) : (
                              <Badge bg="danger">incorrect</Badge>
                            )}
                          </td>
                          <td>{item.attempted_at ? formateDatemdy(item.attempted_at) : 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowQuizModal(false);
            setSelectedQuiz(null);
          }}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />

      {/* Change Password Modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => {
          setShowPasswordModal(false);
          setNewPassword("");
        }}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Student Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Changing password for: <strong>{studentData?.first_name} {studentData?.last_name}</strong>
          </p>
          <Form.Group className="mb-3">
            <Form.Label>New Password <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              minLength={6}
            />
            <Form.Text className="text-muted">
              Password must be at least 6 characters
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowPasswordModal(false);
            setNewPassword("");
          }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleChangePassword}
            disabled={loadingPassword || !newPassword}
          >
            {loadingPassword ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Changing...
              </>
            ) : (
              "Change Password"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer />
    </>
  );
};

export default StudentDetails;